export type BusinessPlan = 'Free' | 'Starter' | 'Professional' | 'Enterprise';

export interface AddressDto {
  street: string;
  streetNumber?: string | null;
  city: string;
  postalCode: string;
  country: string;
}

export interface ContactPersonDto {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string | null;
}

export interface TenantDto {
  id: string;
  name: string;
  address: AddressDto;
  businessPlan: BusinessPlan;
  contactPerson: ContactPersonDto;
  domain: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string | null;

  uidNumber?: string | null;
  iban?: string | null;
  bankName?: string | null;
  companyRegistration?: string | null;
  websiteUrl?: string | null;

  logoUrl?: string | null;
  primaryColor?: string | null;
  accentColor?: string | null;

  timeZone?: string | null;
  language?: string | null;

  defaultVatRate?: number | null;
  defaultPaymentTermDays?: number | null;
  defaultOfferValidityDays?: number | null;
  invoiceNumberPrefix?: string | null;
  offerNumberPrefix?: string | null;
  invoiceNumberStart?: number | null;
  invoiceNumberResetYearly?: boolean;
  offerNumberStart?: number | null;
  offerNumberResetYearly?: boolean;

  personalNumberPrefix?: string | null;
  personalNumberSeparator?: string | null;
  personalNumberIncludeYear?: boolean;
  personalNumberSequenceLength?: number | null;

  /** Official logbook (FAFD-68): drivers may mark a trip as private. */
  allowPrivateTrips?: boolean;
  /** Ending a trip needs these details (FAFD-68); a field that is not required raises no logbook issue. */
  tripStartRequired?: boolean;
  tripDestinationRequired?: boolean;
  tripPurposeRequired?: boolean;
  /** The logbook flags trips outside the stamped working time (FAFD-68). */
  flagTripsOutsideWorkingTime?: boolean;
}

export interface UpdateTenantSettingsRequest {
  name: string;
  address: AddressDto;
  contactPerson: ContactPersonDto;

  uidNumber?: string | null;
  iban?: string | null;
  bankName?: string | null;
  companyRegistration?: string | null;
  websiteUrl?: string | null;

  logoUrl?: string | null;
  primaryColor?: string | null;
  accentColor?: string | null;

  timeZone?: string | null;
  language?: string | null;

  defaultVatRate?: number | null;
  defaultPaymentTermDays?: number | null;
  defaultOfferValidityDays?: number | null;
  invoiceNumberPrefix?: string | null;
  offerNumberPrefix?: string | null;
  invoiceNumberStart?: number | null;
  invoiceNumberResetYearly?: boolean;
  offerNumberStart?: number | null;
  offerNumberResetYearly?: boolean;

  personalNumberPrefix?: string | null;
  personalNumberSeparator?: string | null;
  personalNumberIncludeYear?: boolean;
  personalNumberSequenceLength?: number | null;

  /** Official logbook (FAFD-68): drivers may mark a trip as private. */
  allowPrivateTrips?: boolean;
  /** Ending a trip needs these details (FAFD-68); a field that is not required raises no logbook issue. */
  tripStartRequired?: boolean;
  tripDestinationRequired?: boolean;
  tripPurposeRequired?: boolean;
  /** The logbook flags trips outside the stamped working time (FAFD-68). */
  flagTripsOutsideWorkingTime?: boolean;
}

export const TENANT_SETTINGS_LIMITS = {
  name: 200,
  street: 200,
  streetNumber: 20,
  city: 100,
  postalCode: 20,
  country: 100,
  firstName: 100,
  lastName: 100,
  email: 255,
  phone: 50,
  uidNumber: 20,
  iban: 34,
  bankName: 100,
  companyRegistration: 200,
  websiteUrl: 200,
  logoUrl: 500,
  color: 20,
  timeZone: 100,
  language: 20,
  invoiceNumberPrefix: 20,
  offerNumberPrefix: 20,
  personalNumberPrefix: 20,
  personalNumberSeparator: 5,
} as const;

export const NUMBER_START_MIN = 1;
export const NUMBER_START_MAX = 9999;

export const PERSONAL_NUMBER_SEQUENCE_MIN = 1;
export const PERSONAL_NUMBER_SEQUENCE_MAX = 10;

export const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const URL_PATTERN = /^https?:\/\/.+/i;

export const TIMEZONE_SUGGESTIONS = [
  'Europe/Vienna',
  'Europe/Berlin',
  'Europe/Zurich',
  'Europe/Ljubljana',
  'Europe/Prague',
  'Europe/Budapest',
];

export const LANGUAGE_SUGGESTIONS = [
  { value: 'de-AT', label: 'Deutsch (Österreich)' },
  { value: 'de-DE', label: 'Deutsch (Deutschland)' },
  { value: 'de-CH', label: 'Deutsch (Schweiz)' },
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'sl-SI', label: 'Slovenščina' },
  { value: 'tr-TR', label: 'Türkçe' },
  { value: 'ro-RO', label: 'Română' },
];
